import React, { useCallback, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import type { Element } from 'hast';
import type { Components } from 'streamdown';
import { RecordChip } from 'components/record-chip/RecordChip';
import { resolveRecordFromPath } from 'utils/globalSearchRoutes';
import { useAiChatInternalLinkHandler } from '../../aiChatInternalLinkContext';
import { textOf } from '../../utils/messageClipboard';
import { resolveAiChatLink } from './aiChatLinkResolver';
import DownloadableTable from './DownloadableTable';
import { isModifiedClick } from './isModifiedClick';
import { useExternalLinkConfirmation } from './useExternalLinkConfirmation';

type MarkdownProps<Tag extends keyof JSX.IntrinsicElements> = React.ComponentPropsWithoutRef<Tag> & {
    node?: unknown;
};

const AI_CHAT_LINK_CLASS_NAME = 'text-link hover:underline';
// An answer's headings are its section labels.
const SECTION_LABEL_CLASS_NAME = 'text-label text-foreground-tertiary mt-4 mb-1.5 text-balance uppercase';
const LIST_CLASS_NAME = 'my-2 ps-5 text-pretty marker:text-muted-foreground [&_ol]:my-1 [&_ul]:my-1';
// A table's cells; those at the edges of the Figure's box keep clear of its border.
const TABLE_CELL_CLASS_NAME = 'px-2 py-1.5 first:ps-3 last:pe-3';
const AI_CHAT_LINK_PROPS = {
    target: '_blank',
    rel: 'noopener noreferrer',
} as const;

export const useAiChatMarkdownComponents = (): Components => {
    const onInternalLinkNavigate = useAiChatInternalLinkHandler();

    const handleInternalLinkClick = useCallback(
        (event: React.MouseEvent<HTMLAnchorElement>) => {
            // A modified click opens a new tab and leaves the current page — and with it the
            // surface the link was clicked in — exactly where it was.
            if (isModifiedClick(event)) {
                return;
            }

            onInternalLinkNavigate();
        },
        [onInternalLinkNavigate]
    );

    const handleExternalLinkClick = useExternalLinkConfirmation();

    return useMemo(
        () => ({
            h1: ({ children }: MarkdownProps<'h1'>) => <h1 className={SECTION_LABEL_CLASS_NAME}>{children}</h1>,
            h2: ({ children }: MarkdownProps<'h2'>) => <h2 className={SECTION_LABEL_CLASS_NAME}>{children}</h2>,
            h3: ({ children }: MarkdownProps<'h3'>) => <h3 className={SECTION_LABEL_CLASS_NAME}>{children}</h3>,
            h4: ({ children }: MarkdownProps<'h4'>) => <h4 className={SECTION_LABEL_CLASS_NAME}>{children}</h4>,
            h5: ({ children }: MarkdownProps<'h5'>) => <h5 className={SECTION_LABEL_CLASS_NAME}>{children}</h5>,
            h6: ({ children }: MarkdownProps<'h6'>) => <h6 className={SECTION_LABEL_CLASS_NAME}>{children}</h6>,
            p: ({ children }: MarkdownProps<'p'>) => (
                <p className="text-body1 mt-0 mb-2 leading-[1.6] text-pretty">{children}</p>
            ),
            // Streamdown wraps each word of an answer still arriving in a span of its own, which the streaming-text
            // recipe reveals in turn (`MessageTextPart`). Every other span passes through.
            span: ({ className, children, ...props }: MarkdownProps<'span'>) => (
                <span className={'data-sd-animate' in props ? 't-stream-w' : className}>{children}</span>
            ),
            a: ({ href, children, node }: MarkdownProps<'a'>) => {
                const resolution = resolveAiChatLink(href);

                if (resolution.kind === 'internal' || resolution.kind === 'record') {
                    // An internal link opens in this tab and tells the surface; a bare record path opens a new
                    // tab, so the conversation the link was read in stays where it is. React Router leaves a
                    // targeted link to the browser instead of routing it in place.
                    const linkProps =
                        resolution.kind === 'internal'
                            ? { 'data-ai-chat-link': 'internal', onClick: handleInternalLinkClick }
                            : { 'data-ai-chat-link': 'record', ...AI_CHAT_LINK_PROPS };
                    const record = resolveRecordFromPath(resolution.to);

                    // A link to a record is its chip, which links to the record's own route.
                    return record ? (
                        <RecordChip
                            entityType={record.entityType}
                            id={record.id}
                            label={textOf(node as Element)}
                            {...linkProps}
                        />
                    ) : (
                        <RouterLink to={resolution.to} className={AI_CHAT_LINK_CLASS_NAME} {...linkProps}>
                            {children}
                        </RouterLink>
                    );
                }

                if (resolution.kind === 'phone') {
                    return (
                        <a
                            href={resolution.href}
                            data-ai-chat-link="phone"
                            className={AI_CHAT_LINK_CLASS_NAME}
                            {...AI_CHAT_LINK_PROPS}
                        >
                            {children}
                        </a>
                    );
                }

                if (resolution.kind === 'email') {
                    return (
                        <a
                            href={resolution.href}
                            data-ai-chat-link="email"
                            className={AI_CHAT_LINK_CLASS_NAME}
                            {...AI_CHAT_LINK_PROPS}
                        >
                            {children}
                        </a>
                    );
                }

                if (resolution.kind === 'external') {
                    return (
                        <a
                            href={resolution.href}
                            data-ai-chat-link="external"
                            className={AI_CHAT_LINK_CLASS_NAME}
                            {...AI_CHAT_LINK_PROPS}
                            onClick={(event) => handleExternalLinkClick(event, resolution.href)}
                        >
                            {children}
                        </a>
                    );
                }

                return <span>{children}</span>;
            },
            ul: ({ children }: MarkdownProps<'ul'>) => <ul className={LIST_CLASS_NAME}>{children}</ul>,
            ol: ({ children }: MarkdownProps<'ol'>) => <ol className={LIST_CLASS_NAME}>{children}</ol>,
            li: ({ children }: MarkdownProps<'li'>) => (
                // A record chip's 44px touch area is twice its line: on touch its bullet is spaced for it.
                <li className="text-body1 mb-1.5 ps-0.5 pointer-coarse:has-[.hit-area]:mb-5.5 [&_ol]:mt-1.5 [&_ul]:mt-1.5 [&>p]:mb-0 [&>p]:inline">
                    {children}
                </li>
            ),
            table: ({ children, node }: MarkdownProps<'table'>) => (
                <DownloadableTable node={node as Element | undefined}>{children}</DownloadableTable>
            ),
            thead: ({ children }: MarkdownProps<'thead'>) => (
                // The header row is no body row: no rule above it, no stripe under the pointer.
                <thead className="bg-surface-subtle text-caption text-foreground-tertiary [&>tr]:border-t-0 [&>tr]:hover:bg-transparent">
                    {children}
                </thead>
            ),
            tbody: ({ children }: MarkdownProps<'tbody'>) => <tbody>{children}</tbody>,
            tr: ({ children }: MarkdownProps<'tr'>) => (
                <tr className="border-divider-soft hover:bg-row-stripe border-t">{children}</tr>
            ),
            th: ({ children }: MarkdownProps<'th'>) => (
                <th scope="col" className={`${TABLE_CELL_CLASS_NAME} text-start font-medium`}>
                    {children}
                </th>
            ),
            td: ({ children }: MarkdownProps<'td'>) => <td className={TABLE_CELL_CLASS_NAME}>{children}</td>,
            blockquote: ({ children }: MarkdownProps<'blockquote'>) => (
                <blockquote className="border-fg/20 text-fg-secondary my-2 border-s-4 ps-4 italic">
                    {children}
                </blockquote>
            ),
            hr: () => <hr className="border-divider my-4" />,
            strong: ({ children }: MarkdownProps<'strong'>) => <strong className="font-bold">{children}</strong>,
            em: ({ children }: MarkdownProps<'em'>) => <em className="italic">{children}</em>,
            del: ({ children }: MarkdownProps<'del'>) => <del className="line-through">{children}</del>,
        }),
        [handleExternalLinkClick, handleInternalLinkClick]
    );
};
